// @flow

import transformReduxParamsToSearch from '../../queries/transformReduxParamsToSearchRequest';
import {srsParams as getSrsParams} from '../../utils/searchParamsUtils';
import {
    ADL_DEFAULT_FILTERS_DATA,
    ADL_DEFAULT_LOCATION_DATA,
    ADL_DEFAULT_PAGE_NAME_NO_RESULTS,
    ADL_DEFAULT_PAGE_NAME_WORLD_SEARCH,
    ADL_DEFAULT_VALUE,
    FILTER_GROUP_ID_TO_ADL,
    FILTER_GROUP_KEY_TO_ID,
    FILTERS_ADL_FIELDS,
    GTS_SORT_TO_ADL,
    INITIAL_ANALYTICS_DATA_LAYER,
    LOCATION_ADL_FIELDS,
    TRIPS_ADL_FIELDS
} from './constants';

import type {
    AnalyticsDataLayerType,
    FiltersADLShape,
    LocationADLShape,
    SortADLField,
    TripADLShape
} from './constants';
import type {
    ParsedSearchParams,
    Geography,
    SearchQueryResults
} from '../../queries/searchQuery';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import type {Store} from '../../reducers';

export const getOrDefault = (
    value: any,
    defaultValue: string = ADL_DEFAULT_VALUE
): string =>
    value !== null && typeof value !== 'undefined' && value !== ''
        ? value.toString() || defaultValue
        : defaultValue;

const getLBSDataFromBreadcrumbs = (
    breadcrumbs: Geography[]
): $Shape<LocationADLShape> => {
    if (!breadcrumbs || !breadcrumbs.length) return {};

    return breadcrumbs.reduce(
        (
            data,
            {
                countryCode,
                name: breadcrumbName,
                primaryGeoType,
                location: breadcrumbLocation
            }
        ) => {
            switch (primaryGeoType) {
                case LOCATION_ADL_FIELDS.LOCALITY:
                    data[LOCATION_ADL_FIELDS.LOCALITY] = getOrDefault(
                        breadcrumbName
                    );
                    break;
                case LOCATION_ADL_FIELDS.ADMINISTRATIVE_DIVISION_LEVEL_1:
                    data[LOCATION_ADL_FIELDS.ADMIN_1] = getOrDefault(
                        breadcrumbName
                    );
                    break;
                case LOCATION_ADL_FIELDS.POSTALCODE:
                    data[LOCATION_ADL_FIELDS.POSTALCODE] = getOrDefault(
                        breadcrumbName
                    );
                    break;
                case LOCATION_ADL_FIELDS.COUNTRY:
                    data[LOCATION_ADL_FIELDS.COUNTRY] = getOrDefault(
                        countryCode
                    );
                    break;
                case LOCATION_ADL_FIELDS.POINT_OF_INTEREST:
                    if (
                        breadcrumbLocation &&
                        breadcrumbLocation.latitude &&
                        breadcrumbLocation.longitude
                    ) {
                        data[LOCATION_ADL_FIELDS.LATITUDE] = getOrDefault(
                            breadcrumbLocation.latitude
                        );
                        data[LOCATION_ADL_FIELDS.LONGITUDE] = getOrDefault(
                            breadcrumbLocation.longitude
                        );
                    }

                    break;
                default:
                    break;
            }

            return data;
        },
        {}
    );
};

export const getLocationData = (geography: ?Geography): LocationADLShape => {
    if (!geography) return ADL_DEFAULT_LOCATION_DATA;

    const {
        location,
        name,
        description,
        lbsId,
        primaryGeoType,
        breadcrumbs = []
    } = geography;
    const {latitude, longitude} = location || {};

    return {
        ...ADL_DEFAULT_LOCATION_DATA,
        [LOCATION_ADL_FIELDS.LOCALITY]: getOrDefault(name),
        [LOCATION_ADL_FIELDS.LATITUDE]: getOrDefault(latitude),
        [LOCATION_ADL_FIELDS.LONGITUDE]: getOrDefault(longitude),
        [LOCATION_ADL_FIELDS.SEARCHTERM]: getOrDefault(description),
        [LOCATION_ADL_FIELDS.LBS_UUID]: getOrDefault(lbsId),
        [LOCATION_ADL_FIELDS.GEO_TYPE]: getOrDefault(primaryGeoType),
        ...getLBSDataFromBreadcrumbs(breadcrumbs)
    };
};

/**
 *
 * @param filterGroups
 * @param selectedFilters is used just to determine which filters should be treated as checked when you build
 * filters adl based on query params
 * @returns {FiltersADLShape|{[p: string]: string}}
 */
export const getFiltersData = (
    filterGroups: ?(FilterGroupType[]),
    selectedFilters?: string[]
): FiltersADLShape => {
    if (!filterGroups) return ADL_DEFAULT_FILTERS_DATA;
    const adlData = {};

    filterGroups.forEach(({filters}) => {
        filters.forEach(
            ({checked, filter: {id, groupId, refineByQueryArgument}}) => {
                if (
                    selectedFilters
                        ? selectedFilters.includes(refineByQueryArgument)
                        : checked
                ) {
                    const refineValue =
                        adlData[FILTERS_ADL_FIELDS.REFINE_VALUE] || [];
                    const refineBucket =
                        adlData[FILTERS_ADL_FIELDS.REFINE_BUCKET] || [];
                    adlData[
                        FILTERS_ADL_FIELDS.REFINE_VALUE
                    ] = refineValue.concat(id);

                    if (!refineBucket || !refineBucket.includes(groupId)) {
                        adlData[
                            FILTERS_ADL_FIELDS.REFINE_BUCKET
                        ] = refineBucket.concat(
                            FILTER_GROUP_KEY_TO_ID[groupId] || groupId
                        );
                    }

                    const adlName = FILTER_GROUP_ID_TO_ADL[groupId];
                    if (adlName) {
                        const adlValue = adlData[adlName] || [];
                        adlData[adlName] = adlValue.concat(id);
                    }
                }
            }
        );
    });

    return {
        [FILTERS_ADL_FIELDS.REFINE_VALUE]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.REFINE_VALUE]
        ),
        [FILTERS_ADL_FIELDS.REFINE_BUCKET]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.REFINE_BUCKET]
        ),
        [FILTERS_ADL_FIELDS.BOOKING]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.BOOKING]
        ),
        [FILTERS_ADL_FIELDS.PROPERTY_TYPE]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.PROPERTY_TYPE]
        ),
        [FILTERS_ADL_FIELDS.LOCATION_TYPE]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.LOCATION_TYPE]
        ),
        [FILTERS_ADL_FIELDS.MISC]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.MISC]
        ),
        [FILTERS_ADL_FIELDS.FEATURES]: getOrDefault(
            adlData[FILTERS_ADL_FIELDS.FEATURES]
        )
    };
};

export const getSelectedSort = (sort: string): SortADLField =>
    GTS_SORT_TO_ADL[sort] || GTS_SORT_TO_ADL.DEFAULT_SORT;

export const getTripsData = (params: ParsedSearchParams): TripADLShape => {
    const {q, coreFilters, dates} = params || {};
    const {
        adults,
        children,
        pets,
        minBedrooms,
        maxBedrooms,
        minBathrooms,
        maxBathrooms,
        minPrice,
        maxPrice,
        minSleeps
    } = coreFilters || {};
    const {arrivalDate, departureDate} = dates || {};
    const selectedSort = getSelectedSort('DEFAULT_SORT');

    return {
        [TRIPS_ADL_FIELDS.KEYWORDS]: getOrDefault(q),
        [TRIPS_ADL_FIELDS.NUM_ADULTS]: getOrDefault(adults, '0'),
        [TRIPS_ADL_FIELDS.NUM_CHILDREN]: getOrDefault(children, '0'),
        [TRIPS_ADL_FIELDS.NUM_INFANTS]: ADL_DEFAULT_VALUE,
        [TRIPS_ADL_FIELDS.START_DATE]: getOrDefault(arrivalDate),
        [TRIPS_ADL_FIELDS.END_DATE]: getOrDefault(departureDate),
        [TRIPS_ADL_FIELDS.MIN_BATHS]: getOrDefault(minBathrooms),
        [TRIPS_ADL_FIELDS.MAX_BATHS]: getOrDefault(maxBathrooms),
        [TRIPS_ADL_FIELDS.MIN_BEDS]: getOrDefault(minBedrooms),
        [TRIPS_ADL_FIELDS.MAX_BEDS]: getOrDefault(maxBedrooms),
        [TRIPS_ADL_FIELDS.MIN_PRICE]: getOrDefault(minPrice),
        [TRIPS_ADL_FIELDS.MAX_PRICE]: getOrDefault(maxPrice),
        [TRIPS_ADL_FIELDS.MIN_SLEEPS]: getOrDefault(
            minSleeps || (adults || 0) + (children || 0),
            '0'
        ),
        [TRIPS_ADL_FIELDS.WITH_PETS]: getOrDefault(!!pets, 'false'),
        [TRIPS_ADL_FIELDS.SORT]: getOrDefault(selectedSort)
    };
};

export const getPageName = (adl: $Shape<AnalyticsDataLayerType>): string => {
    const {lbsuuid, searchterm, numresults, pagename} = adl;
    if (numresults && Number(numresults) === 0) {
        return ADL_DEFAULT_PAGE_NAME_NO_RESULTS;
    } else if (lbsuuid && lbsuuid === '-1') {
        return ADL_DEFAULT_PAGE_NAME_WORLD_SEARCH;
    } else if (lbsuuid && searchterm) {
        return `search_${lbsuuid}_${searchterm}`;
    }
    // fallback, shouldn't happen since this would be for a GBS only region
    return getOrDefault(pagename);
};

export const getSearchTerm = (
    searchTerm?: string,
    parsedParams?: ParsedSearchParams
): string => {
    if (
        (!searchTerm || searchTerm === ADL_DEFAULT_VALUE) &&
        parsedParams &&
        parsedParams.q
    ) {
        return parsedParams.q;
    }

    return getOrDefault(searchTerm);
};

export const buildAdlFragmentFromParams = (
    store: Store,
    prevFilterGroups?: ?(FilterGroupType[])
) => {
    const searchParams = store.getState().searchParams.params;
    const parsedParams = transformReduxParamsToSearch(
        getSrsParams(searchParams)
    );

    const tripsADL = getTripsData(parsedParams);
    const filtersADL = getFiltersData(prevFilterGroups, searchParams.filter);
    const pagination = getOrDefault(parsedParams.paging.page);

    return {...tripsADL, ...filtersADL, pagination};
};

/** build AnalyticsDataLayer object from results */
export default (
    results: SearchQueryResults,
    baseAnalyticsDataLayer: $Shape<AnalyticsDataLayerType>
): AnalyticsDataLayerType => {
    const {parsedParams} = results || {};

    if (!results || !parsedParams) {
        return {
            ...INITIAL_ANALYTICS_DATA_LAYER,
            ...baseAnalyticsDataLayer
        };
    }
    const {
        pinnedListing,
        geography,
        listings,
        filterGroups,
        page = 1,
        pageSize = 0,
        resultCount = 0
    } = results;
    const tripsADL = getTripsData(parsedParams);
    const lbsADL = getLocationData(geography);
    const filtersADL = getFiltersData(filterGroups);
    const pagination = getOrDefault(page);
    const numresults = getOrDefault(resultCount);
    const numresultsdisplayed = getOrDefault(
        resultCount < pageSize ? resultCount : pageSize
    );
    const pagename = getPageName({
        ...lbsADL,
        numresults
    });
    const searchterm = getSearchTerm(lbsADL.searchterm, parsedParams);
    const listingids = getOrDefault(
        (listings || []).slice(0, 5).map((listing) => listing.listingId)
    );
    const pinnedlistingids = getOrDefault(
        pinnedListing ? pinnedListing.listing.listingId : null
    );

    return {
        ...INITIAL_ANALYTICS_DATA_LAYER,
        ...baseAnalyticsDataLayer,
        ...tripsADL,
        ...lbsADL,
        ...filtersADL,
        listingids,
        numresults,
        numresultsdisplayed,
        pagename,
        pagination,
        pinnedlistingids,
        searchterm
    };
};
